import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useWorkspaceData } from "shared/hooks";
import {
	NotistackServices,
	TaskCreationRealtimeEvent,
	TaskItemRealtimeEvent,
	TaskRealtimeEvent,
	TaskSubtaskRealtimeEvent,
	TaskSubtaskStatusRealtimeEvent,
	TaskSubtaskToolCallRealtimeEvent,
	TeamLeaderServices,
	TeamTaskDTO,
	WorkspaceTasksPayloadDTO,
} from "shared/services";
import { HandleError } from "shared/utils";
import { applySubtaskRealtimeUpdate, createEmptyTaskBoardState, normalizeWorkspaceTasks } from "../taskBoard.utils";
import { TaskBoardState } from "../types";

const WORKSPACE_CACHE_TTL_MS = 30_000;

interface LoadOptions {
	silent?: boolean;
	force?: boolean;
	preferCache?: boolean;
}

interface CacheEntry<T> {
	data: T;
	fetchedAt: number;
}

type TaskBoardAction =
	| { type: "RESET" }
	| { type: "SET_LOADING_TASKS"; value: boolean }
	| { type: "SET_LOADING_MEMBERS"; value: boolean }
	| { type: "LOAD_WORKSPACE_DATA"; tasks: TeamTaskDTO[]; memberNamesById: Record<string, string> }
	| { type: "SELECT_TASK"; taskId: string | null }
	| {
			type: "APPLY_SUBTASK_EVENT";
			event: {
				taskId: string;
				taskItemId: string;
				subtaskId: string;
				status: TaskSubtaskRealtimeEvent["status"];
				statusDescription: string;
				capacityUsed: number;
				usedMcps: string[];
				updatedAt: string;
				completedAt: string | null;
				reviewStatus?: "approved" | "rejected" | "needs_changes" | null;
			};
	  }
	| { type: "APPLY_TOOL_CALL"; event: TaskSubtaskToolCallRealtimeEvent };

const reducer = (state: TaskBoardState, action: TaskBoardAction): TaskBoardState => {
	switch (action.type) {
		case "RESET":
			return createEmptyTaskBoardState();
		case "SET_LOADING_TASKS":
			return {
				...state,
				loadingTasks: action.value,
			};
		case "SET_LOADING_MEMBERS":
			return {
				...state,
				loadingMembers: action.value,
			};
		case "LOAD_WORKSPACE_DATA": {
			const normalized = normalizeWorkspaceTasks(action.tasks);
			const selectedTaskId =
				state.selectedTaskId && normalized.taskById[state.selectedTaskId] ? state.selectedTaskId : (normalized.taskIds[0] ?? null);

			return {
				...state,
				...normalized,
				memberNamesById: action.memberNamesById,
				selectedTaskId,
			};
		}
		case "SELECT_TASK":
			return {
				...state,
				selectedTaskId: action.taskId,
			};
		case "APPLY_SUBTASK_EVENT": {
			const nextState = applySubtaskRealtimeUpdate(state, action.event);
			return nextState ?? state;
		}
		case "APPLY_TOOL_CALL": {
			const { event } = action;
			const subtaskId = event.subtaskId;
			if (!subtaskId) return state;
			const next = { ...state.toolCallBySubtaskId };
			// Mantemos sempre o último evento (start sobrescreve end anterior e vice-versa)
			// para o componente saber se há uma tool em andamento (phase=start) ou se a
			// última terminou (phase=end com success/duration).
			next[subtaskId] = {
				toolName: event.toolName,
				mcpSlug: event.mcpSlug,
				mcpName: event.mcpName,
				phase: event.phase,
				startedAt: event.startedAt,
				durationMs: event.durationMs,
				success: event.success,
				error: event.error,
			};
			return { ...state, toolCallBySubtaskId: next };
		}
		default:
			return state;
	}
};

const isFreshCache = <T>(entry: CacheEntry<T> | undefined, ttlMs: number): entry is CacheEntry<T> => {
	if (!entry) return false;
	return Date.now() - entry.fetchedAt < ttlMs;
};

const toMemberMap = (members: WorkspaceTasksPayloadDTO["members"]): Record<string, string> => {
	return members.reduce(
		(acc, member) => {
			acc[member.id] = member.name;
			return acc;
		},
		{} as Record<string, string>,
	);
};

const patchCachedTaskListBySubtaskEvent = (
	tasks: TeamTaskDTO[],
	event: {
		taskId: string;
		taskItemId: string;
		subtaskId: string;
		status: TaskSubtaskRealtimeEvent["status"];
		statusDescription: string;
		capacityUsed: number;
		usedMcps: string[];
		updatedAt: string;
		completedAt: string | null;
	},
): TeamTaskDTO[] => {
	let found = false;

	const next = tasks.map((task) => {
		if (task.id !== event.taskId) return task;

		return {
			...task,
			updatedAt: event.updatedAt,
			items: task.items.map((item) => {
				if (item.id !== event.taskItemId) return item;

				return {
					...item,
					updatedAt: event.updatedAt,
					subtasks: item.subtasks.map((subtask) => {
						if (subtask.id !== event.subtaskId) return subtask;
						found = true;

						return {
							...subtask,
							status: event.status,
							statusDescription: event.statusDescription,
							capacityUsed: event.capacityUsed,
							usedMcps: event.usedMcps,
							updatedAt: event.updatedAt,
							completedAt: event.completedAt,
						};
					}),
				};
			}),
		};
	});

	return found ? next : tasks;
};

export const useTaskBoardStore = () => {
	const { workspace, teams, loadingTeams, loadingWorkspace } = useWorkspaceData();
	const [state, dispatch] = useReducer(reducer, undefined, createEmptyTaskBoardState);

	const stateRef = useRef(state);
	const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const workspaceCacheRef = useRef<Record<string, CacheEntry<WorkspaceTasksPayloadDTO>>>({});
	const inFlightWorkspaceRequestsRef = useRef<Record<string, Promise<WorkspaceTasksPayloadDTO>>>({});

	useEffect(() => {
		stateRef.current = state;
	}, [state]);

	const workspaceId = workspace ? String(workspace.id) : null;

	const loadWorkspaceTasks = useCallback(
		async (options: LoadOptions = {}) => {
			if (!workspaceId) return { tasks: [], members: [] } as WorkspaceTasksPayloadDTO;

			const cacheKey = workspaceId;
			const preferCache = options.preferCache ?? true;
			let requestPromise: Promise<WorkspaceTasksPayloadDTO> | undefined;

			if (!options.silent) {
				dispatch({ type: "SET_LOADING_TASKS", value: true });
				dispatch({ type: "SET_LOADING_MEMBERS", value: true });
			}

			try {
				const cached = workspaceCacheRef.current[cacheKey];
				if (!options.force && preferCache && isFreshCache(cached, WORKSPACE_CACHE_TTL_MS)) {
					dispatch({
						type: "LOAD_WORKSPACE_DATA",
						tasks: cached.data.tasks,
						memberNamesById: toMemberMap(cached.data.members),
					});
					return cached.data;
				}

				requestPromise = inFlightWorkspaceRequestsRef.current[cacheKey];
				if (!requestPromise) {
					requestPromise = TeamLeaderServices.getTasks(workspaceId);
					inFlightWorkspaceRequestsRef.current[cacheKey] = requestPromise;
				}

				const payload = await requestPromise;
				workspaceCacheRef.current[cacheKey] = {
					data: payload,
					fetchedAt: Date.now(),
				};

				dispatch({
					type: "LOAD_WORKSPACE_DATA",
					tasks: payload.tasks,
					memberNamesById: toMemberMap(payload.members),
				});

				return payload;
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar tasks do workspace");
				return { tasks: [], members: [] } as WorkspaceTasksPayloadDTO;
			} finally {
				if (requestPromise && inFlightWorkspaceRequestsRef.current[cacheKey] === requestPromise) {
					delete inFlightWorkspaceRequestsRef.current[cacheKey];
				}

				if (!options.silent) {
					dispatch({ type: "SET_LOADING_TASKS", value: false });
					dispatch({ type: "SET_LOADING_MEMBERS", value: false });
				}
			}
		},
		[workspaceId],
	);

	const scheduleWorkspaceReload = useCallback(() => {
		if (!workspaceId) return;
		if (refreshTimeoutRef.current) return;

		refreshTimeoutRef.current = setTimeout(() => {
			refreshTimeoutRef.current = null;
			void loadWorkspaceTasks({ silent: true, force: true, preferCache: false });
		}, 400);
	}, [loadWorkspaceTasks, workspaceId]);

	const handleSubtaskRealtimeEvent = useCallback(
		(event: TaskSubtaskRealtimeEvent | TaskSubtaskStatusRealtimeEvent) => {
			const currentState = stateRef.current;
			if (!currentState.subtaskById[event.subtaskId] || !currentState.taskItemById[event.taskItemId] || !currentState.taskById[event.taskId]) {
				scheduleWorkspaceReload();
				return;
			}

			if (workspaceId) {
				const cached = workspaceCacheRef.current[workspaceId];
				if (cached) {
					workspaceCacheRef.current[workspaceId] = {
						...cached,
						data: {
							...cached.data,
							tasks: patchCachedTaskListBySubtaskEvent(cached.data.tasks, {
								taskId: event.taskId,
								taskItemId: event.taskItemId,
								subtaskId: event.subtaskId,
								status: event.status,
								statusDescription: event.statusDescription,
								capacityUsed: event.capacityUsed,
								usedMcps: event.usedMcps,
								updatedAt: event.updatedAt,
								completedAt: event.completedAt,
							}),
						},
					};
				}
			}

			dispatch({
				type: "APPLY_SUBTASK_EVENT",
				event: {
					taskId: event.taskId,
					taskItemId: event.taskItemId,
					subtaskId: event.subtaskId,
					status: event.status,
					statusDescription: event.statusDescription,
					capacityUsed: event.capacityUsed,
					usedMcps: event.usedMcps,
					updatedAt: event.updatedAt,
					completedAt: event.completedAt,
					reviewStatus: (event as any).reviewStatus,
				},
			});
		},
		[scheduleWorkspaceReload, workspaceId],
	);

	useEffect(() => {
		if (!workspaceId) {
			dispatch({ type: "RESET" });
			return;
		}

		void loadWorkspaceTasks({ force: false, preferCache: true });
	}, [workspaceId, loadWorkspaceTasks]);

	useEffect(() => {
		if (!workspaceId) return;

		const isFromCurrentWorkspace = (eventWorkspaceId: string) => String(eventWorkspaceId) === workspaceId;

		const onTaskEvent = (event: TaskItemRealtimeEvent | TaskCreationRealtimeEvent | TaskRealtimeEvent) => {
			if (!isFromCurrentWorkspace(event.workspaceId)) {
				return;
			}

			delete workspaceCacheRef.current[workspaceId];
			scheduleWorkspaceReload();
		};

		const subscription = TeamLeaderServices.subscribeToTaskUpdates(workspaceId, {
			onSubtaskUpdated: (event) => {
				if (!isFromCurrentWorkspace(event.workspaceId)) return;
				handleSubtaskRealtimeEvent(event);
			},
			onSubtaskStatusUpdated: (event) => {
				if (!isFromCurrentWorkspace(event.workspaceId)) return;
				handleSubtaskRealtimeEvent(event);
			},
			onSubtaskToolCall: (event) => {
				// tool_call não traz workspaceId — filtramos por subtaskId já presente no state
				if (!event.subtaskId) return;
				dispatch({ type: "APPLY_TOOL_CALL", event });
			},
			onTaskItemUpdated: onTaskEvent,
			onTaskUpdated: onTaskEvent,
			onTaskCreated: onTaskEvent,
		});

		return () => {
			subscription.stop();
		};
	}, [workspaceId, scheduleWorkspaceReload, handleSubtaskRealtimeEvent]);

	useEffect(() => {
		return () => {
			if (refreshTimeoutRef.current) {
				clearTimeout(refreshTimeoutRef.current);
				refreshTimeoutRef.current = null;
			}
		};
	}, []);

	const setSelectedTaskId = useCallback((taskId: string | null) => {
		dispatch({ type: "SELECT_TASK", taskId });
	}, []);

	const refresh = useCallback(() => {
		void loadWorkspaceTasks({ force: true, preferCache: false });
	}, [loadWorkspaceTasks]);

	const isLoading = useMemo(() => {
		return loadingWorkspace || loadingTeams || state.loadingTasks || state.loadingMembers;
	}, [loadingWorkspace, loadingTeams, state.loadingTasks, state.loadingMembers]);

	return {
		workspace,
		teams,
		state,
		isLoading,
		refresh,
		setSelectedTaskId,
	};
};
